import React from "react";
import PropTypes from "prop-types";
const { selectAuthoritativeLatestSession } = require("./stripeSyncDetailsHelpers");

function formatTimestamp(value) {
  if (!value) {
    return "—";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function formatDuration(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) {
    return "—";
  }

  const start = new Date(startedAt).getTime();
  const finish = new Date(finishedAt).getTime();
  const duration = finish - start;

  if (Number.isNaN(duration)) {
    return "—";
  }

  if (duration < 1000) {
    return `${duration}ms`;
  }

  if (duration < 60000) {
    return `${(duration / 1000).toFixed(1)}s`;
  }

  const minutes = Math.floor(duration / 60000);
  const seconds = Math.floor((duration % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

function getBadge(status, enabled) {
  if (!enabled) {
    return { label: "Disabled", className: "badge-secondary" };
  }

  if (status === "failed") {
    return { label: "Failed", className: "badge-danger" };
  }

  if (status === "running") {
    return { label: "Running", className: "badge-warning" };
  }

  return { label: "Healthy", className: "badge-success" };
}

function StripeSyncDetailsPanel({ summary, schedulerStatus, history, onClose, isLoading }) {
  const runtimeStatus = (summary && summary.status) || (schedulerStatus && schedulerStatus.status) || "idle";
  const enabled = schedulerStatus ? schedulerStatus.enabled : true;
  const badge = getBadge(runtimeStatus, enabled);
  const latestSession = selectAuthoritativeLatestSession(summary, schedulerStatus);
  const lastAttempted = (summary && summary.lastAttemptedSync) || (summary && summary.startedAt) || (schedulerStatus && schedulerStatus.lastAttemptedSync) || null;
  const lastFinished = (summary && summary.finishedAt) || (summary && summary.lastSuccessfulSync) || (schedulerStatus && schedulerStatus.finishedAt) || (schedulerStatus && schedulerStatus.lastSuccessfulSync) || null;
  const lastSuccessful = (summary && summary.lastSuccessfulSync) || (schedulerStatus && schedulerStatus.lastSuccessfulSync) || null;
  const imported = summary && summary.reconciliation && summary.reconciliation.totalImported !== undefined
    ? summary.reconciliation.totalImported
    : (summary && summary.imported !== undefined ? summary.imported : (schedulerStatus && schedulerStatus.totalImported !== undefined ? schedulerStatus.totalImported : 0));
  const alreadyExisting = summary && summary.reconciliation && summary.reconciliation.totalSkipped !== undefined
    ? summary.reconciliation.totalSkipped
    : (summary && summary.alreadyExisting !== undefined ? summary.alreadyExisting : (schedulerStatus && schedulerStatus.totalSkipped !== undefined ? schedulerStatus.totalSkipped : 0));
  const failed = summary && summary.reconciliation && summary.reconciliation.totalFailed !== undefined
    ? summary.reconciliation.totalFailed
    : (summary && summary.failed !== undefined ? summary.failed : (schedulerStatus && schedulerStatus.totalFailed !== undefined ? schedulerStatus.totalFailed : 0));
  const checked = imported + alreadyExisting + failed;
  const trigger = summary && summary.lastSuccessfulSync ? "Manual" : ((schedulerStatus && schedulerStatus.status === "running") || (history && history.length > 0 && history[0].trigger === "Scheduler") ? "Scheduler" : "Manual");

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Stripe Synchronization</h3>
        <button type="button" className="btn btn-link p-0" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      {isLoading && (
        <div className="alert alert-info py-2">
          Refreshing sync details...
        </div>
      )}

      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Scheduler Status</h6>
            <span className={`badge ${badge.className}`}>{badge.label}</span>
          </div>
          <div className="row">
            <div className="col-6 mb-2"><small className="text-muted">Scheduler Enabled</small><div>{enabled ? "Yes" : "No"}</div></div>
            <div className="col-6 mb-2"><small className="text-muted">Current Mode</small><div>{schedulerStatus && schedulerStatus.mode ? schedulerStatus.mode : "Incremental"}</div></div>
            <div className="col-12"><small className="text-muted">Status</small><div>{badge.label}</div></div>
          </div>
        </div>
      </div>

      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-body">
          <h6 className="mb-3">Last Synchronization</h6>
          <div className="row">
            <div className="col-6 mb-2"><small className="text-muted">Started</small><div>{formatTimestamp(lastAttempted)}</div></div>
            <div className="col-6 mb-2"><small className="text-muted">Finished</small><div>{formatTimestamp(lastFinished || lastSuccessful)}</div></div>
            <div className="col-6"><small className="text-muted">Duration</small><div>{formatDuration(lastAttempted, lastFinished || lastSuccessful)}</div></div>
            <div className="col-6"><small className="text-muted">Trigger</small><div>{trigger}</div></div>
          </div>
        </div>
      </div>

      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-body">
          <h6 className="mb-3">Results</h6>
          <div className="row">
            <div className="col-6 mb-2"><small className="text-muted">Checked</small><div>{checked}</div></div>
            <div className="col-6 mb-2"><small className="text-muted">Imported</small><div>{imported}</div></div>
            <div className="col-6"><small className="text-muted">Already Existing</small><div>{alreadyExisting}</div></div>
            <div className="col-6"><small className="text-muted">Failed</small><div>{failed}</div></div>
          </div>
        </div>
      </div>

      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-body">
          <h6 className="mb-3">Latest Imported Purchase</h6>
          <div><small className="text-muted">Latest Stripe Session</small><div>{latestSession && latestSession.stripeSessionId ? latestSession.stripeSessionId : "None"}</div></div>
          <div className="mt-2"><small className="text-muted">Latest Imported Time</small><div>{latestSession && latestSession.createdAt ? formatTimestamp(latestSession.createdAt) : "None"}</div></div>
        </div>
      </div>

      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-body">
          <h6 className="mb-3">Next Scheduled Run</h6>
          <div>{enabled ? (schedulerStatus && schedulerStatus.config ? `${Math.round((schedulerStatus.config.interval || 600000) / 60000)}m` : "Scheduled") : "Automatic synchronization disabled"}</div>
        </div>
      </div>

      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-body">
          <h6 className="mb-3">Last Error</h6>
          <div>{schedulerStatus && schedulerStatus.lastError ? schedulerStatus.lastError : "No errors"}</div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <h6 className="mb-3">Recent Synchronization History</h6>
          {history && history.length > 0 ? history.slice(0, 10).map((entry, index) => (
            <div key={`${entry.timestamp || index}-${index}`} className="border-bottom py-2">
              <div className="d-flex justify-content-between align-items-center">
                <strong>{formatTimestamp(entry.timestamp)}</strong>
                <span className="small text-muted">{entry.trigger || "Manual"}</span>
              </div>
              <div className="small text-muted">Imported {entry.imported || 0}</div>
            </div>
          )) : <div className="text-muted">No recent synchronization history available.</div>}
        </div>
      </div>
    </div>
  );
}

StripeSyncDetailsPanel.propTypes = {
  summary: PropTypes.object,
  schedulerStatus: PropTypes.object,
  history: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default StripeSyncDetailsPanel;
